/* eslint-disable react/display-name */
import React, { useCallback, useEffect, useState } from 'react';

import debounce from 'lodash/debounce';

import { AsyncSelect, components } from '@atlaskit/select';
import Tooltip from '@atlaskit/tooltip';

import { Label } from '@qality/common-components';
import { DEBOUNCE_DELAY_MS } from '@qality/common-config';
import { TestCycle } from '@qality/common-model';
import {
  StorageOfUniqueServices,
  TestCyclesService,
} from '@qality/common-service';

import { OptionWithCheckboxAndSubtext } from '../select-components/OptionWithCheckboxAndSubtext';
import { SelectActionButton } from '../select-components/SelectActionButton';
import { SelectHint } from '../select-components/SelectHint';
import {
  ValueContainerWithOverflow,
  ValueContainerWithOverflowStyles,
} from '../select-components/ValueContainerWithOverflow';
import { TestCycleSelectStyled } from './TestCycleSelect.styled';
import {
  TestCycleSelectGroupedOptions,
  TestCycleSelectOption,
  TestCycleSelectOptions,
} from './TestCycleSelect.types';
import {
  extendSelectOptions,
  mapTestCyclesToSelectGroupedOptions,
  mapTestCyclesToTestCycleOptions,
} from './TestCycleSelect.util';

type Props = {
  onChange: (value: Array<TestCycle>) => void;
  testCyclesIds?: Array<TestCycle['id']>;
  isDisabled?: boolean;
  label?: string;
  tooltip?: string;
};

const HTML_ID = 'qality-test-cycle-select';

const ERROR_MESSAGE =
  'We could not retrieve test cycles. Please try again. If the problem persists, feel free to contact our support.';

const ERROR_MESSAGE_LOAD_MORE =
  'We could not load more projects. Please try again. If the problem persists, feel free to contact our support.';

export function TestCycleSelect({
  onChange,
  testCyclesIds = [],
  isDisabled = false,
  label = null,
  tooltip = null,
}: Props): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);

  const [options, setOptions] = useState<TestCycleSelectGroupedOptions>([]);
  const [value, setValue] = useState<TestCycleSelectOptions>([]);
  const [inputValue, setInputValue] = useState('');

  const [page, setPage] = useState(0);
  const [totalAvailableOptions, setTotalAvailableOptions] = useState(0);

  useEffect(() => {
    loadInitOptions();
  }, []);

  const loadInitOptions = async (): Promise<void> => {
    try {
      setIsLoading(true);

      const { newLoadedOptions, newTotalAvailableOptions } = await loadOptions(
        0,
        ''
      );
      const newValues = await loadValues(testCyclesIds);

      setPage(1);
      setOptions(newLoadedOptions);
      setValue(newValues);
      setTotalAvailableOptions(newTotalAvailableOptions);
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        ERROR_MESSAGE
      );
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreOptions = async (): Promise<void> => {
    try {
      setIsLoading(true);

      const { newLoadedOptions, newTotalAvailableOptions } = await loadOptions(
        page,
        inputValue
      );
      const newOptions = extendSelectOptions(options, newLoadedOptions);

      setPage((p) => p + 1);
      setOptions(newOptions);
      setTotalAvailableOptions(newTotalAvailableOptions);
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        ERROR_MESSAGE_LOAD_MORE
      );
    } finally {
      setIsLoading(false);
    }
  };

  const loadOptions = async (
    currentPage: number,
    testCycleName: string
  ): Promise<{
    newLoadedOptions: TestCycleSelectGroupedOptions;
    newTotalAvailableOptions: number;
  }> => {
    try {
      const data = await TestCyclesService.fetchTestCycles(currentPage, {
        name: testCycleName,
      });

      return {
        newLoadedOptions: mapTestCyclesToSelectGroupedOptions(data.testCycles),
        newTotalAvailableOptions: data.pagination.totalElements,
      };
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        ERROR_MESSAGE
      );
    }
  };

  const loadValues = async (
    ids: Array<TestCycle['id']>
  ): Promise<TestCycleSelectOptions> => {
    if (!testCyclesIds || testCyclesIds.length === 0) {
      return [];
    }

    try {
      const newTestCycles = await TestCyclesService.fetchTestCyclesByIds(ids);

      return mapTestCyclesToTestCycleOptions(newTestCycles);
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        ERROR_MESSAGE
      );
    }
  };

  const loadOptionsDebounced = useCallback(
    debounce(
      (
        currentInputValue: string,
        callback: (newOptions: TestCycleSelectGroupedOptions) => void
      ): void => {
        // lodash/debounce doesn't work with async/await
        // eslint-disable-next-line promise/catch-or-return, promise/no-callback-in-promise
        loadOptions(0, currentInputValue).then(({ newLoadedOptions }) =>
          callback(newLoadedOptions)
        );
      },
      DEBOUNCE_DELAY_MS
    ),
    []
  );

  const areMoreOptionsAvailable = () => {
    const [testCyclesGroup] = options;

    if (!testCyclesGroup) {
      return false;
    }

    return testCyclesGroup.options.length < totalAvailableOptions;
  };

  const handleChange = (newValue: TestCycleSelectOptions) => {
    setValue(newValue);
    onChange?.(newValue.map((tcSelectOption) => tcSelectOption.testCycle));
  };

  const handleInputChange = (newInputValue): void => {
    setInputValue(newInputValue);
  };

  return (
    <TestCycleSelectStyled>
      {label && <Label text={label} />}
      <Tooltip content={tooltip}>
        <AsyncSelect
          id={HTML_ID}
          isLoading={isLoading}
          isDisabled={isDisabled}
          isClearable
          isMulti
          hideSelectedOptions={false}
          closeMenuOnSelect={false}
          placeholder='Select Test Cycle'
          defaultOptions={options}
          loadOptions={loadOptionsDebounced}
          value={value}
          onChange={handleChange}
          onInputChange={handleInputChange}
          noOptionsMessage={() => 'No available Test Cycles'}
          styles={{
            multiValue: (base) => ({
              ...base,
              ...ValueContainerWithOverflowStyles,
            }),
          }}
          components={{
            ValueContainer: ValueContainerWithOverflow,
            Option: (props) => {
              const data = props!.data as TestCycleSelectOption;
              return (
                <OptionWithCheckboxAndSubtext
                  text={data.label}
                  subtext={`(${data.testCycle.project.key}) ${data.testCycle.project.name}`}
                  isChecked={Boolean(
                    value.find((tc) => tc.value === data.value)
                  )}
                  optionProps={props}
                />
              );
            },
            Group: (props) => {
              return (
                <components.Group {...props}>
                  {props!.children}
                  {(() => {
                    if (inputValue.length > 0) {
                      return null;
                    }

                    if (areMoreOptionsAvailable()) {
                      return (
                        <SelectActionButton
                          appearance='link'
                          isLoading={isLoading}
                          onClick={loadMoreOptions}
                        >
                          Load more Test Cycles
                        </SelectActionButton>
                      );
                    }

                    return <SelectHint text='No more Test Cycles to load' />;
                  })()}
                </components.Group>
              );
            },
          }}
        />
      </Tooltip>
    </TestCycleSelectStyled>
  );
}
